// Résumé upload constraints, shared by the careers form (client-side hints)
// and the careers route handler (the checks that count).

export const MAX_RESUME_BYTES = 10 * 1024 * 1024;

export const RESUME_ACCEPT = '.pdf,.doc,.docx';

export type ResumeExtension = 'pdf' | 'doc' | 'docx';

const ALLOWED: ReadonlySet<string> = new Set(['pdf', 'doc', 'docx']);

// Leading bytes for each accepted container. The browser-reported MIME type is
// attacker controlled, so the file body is what we trust.
const SIGNATURES: Record<ResumeExtension, number[]> = {
  pdf: [0x25, 0x50, 0x44, 0x46], // %PDF
  docx: [0x50, 0x4b, 0x03, 0x04], // ZIP local file header
  doc: [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1], // OLE compound file
};

export type ResumeValidation =
  | { ok: true; extension: ResumeExtension }
  | { ok: false; error: string };

export function resumeExtension(filename: string): ResumeExtension | null {
  const dot = filename.lastIndexOf('.');
  if (dot < 0) return null;
  const ext = filename.slice(dot + 1).toLowerCase();
  return ALLOWED.has(ext) ? (ext as ResumeExtension) : null;
}

function startsWith(bytes: Uint8Array, prefix: number[]): boolean {
  if (bytes.length < prefix.length) return false;
  return prefix.every((value, index) => bytes[index] === value);
}

export function validateResume(filename: string, bytes: Uint8Array): ResumeValidation {
  const extension = resumeExtension(filename);
  if (!extension) {
    return { ok: false, error: 'Please attach your résumé as a PDF or Word document.' };
  }
  if (bytes.length === 0) {
    return { ok: false, error: 'The attached file is empty.' };
  }
  if (bytes.length > MAX_RESUME_BYTES) {
    return { ok: false, error: 'Please keep your résumé under 10 MB.' };
  }
  if (!startsWith(bytes, SIGNATURES[extension])) {
    return { ok: false, error: 'That file does not look like a PDF or Word document.' };
  }
  return { ok: true, extension };
}

/**
 * Attachment name for the outgoing email. The applicant's original filename
 * is discarded so nothing they typed reaches a mail client as a path.
 */
export function attachmentFilename(lastName: string, extension: ResumeExtension): string {
  const safe = lastName.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 80);
  return `${safe || 'applicant'}-resume.${extension}`;
}
